import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Button,
	Container,
	CssBaseline,
	Paper,
	Typography,
} from '@material-ui/core';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useDeleteBookMutation,
	useGetBookReferencesCountMutation,
} from '../../../store/apiSlice';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		alignItems: 'center',
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
	},
	title: {
		paddingBottom: theme.spacing(2),
	},
	countText: {
		fontWeight: 700,
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	warning: {
		color: theme.palette.error.main,
	},
	buttons: {
		display: 'flex',
		gap: theme.spacing(2),
		marginTop: theme.spacing(2),
	},
	srOnly: {
		position: 'absolute',
		width: 1,
		height: 1,
		padding: 0,
		margin: -1,
		overflow: 'hidden',
		clip: 'rect(0, 0, 0, 0)',
		border: 0,
	},
}));

export function CatalogueDelete() {
	const classes = useStyles();
	const navigate = useNavigate();
	const { id } = useParams();

	const [
		getBookReferencesCount,
		{ data, isLoading, isError: isCountError },
	] = useGetBookReferencesCountMutation();
	const [deleteBook, { isLoading: isDeleting, isError: isDeleteError }] =
		useDeleteBookMutation();
	const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

	useEffect(() => {
		if (id) {
			getBookReferencesCount({ id });
		}
	}, [id, getBookReferencesCount]);

	const hasCount = typeof data?.count === 'number';
	const countValue = hasCount ? data.count : null;
	const countText = isLoading
		? 'Зареждане на броя потребители...'
		: hasCount
			? `Тази книга е добавена от ${countValue} потребители.`
			: 'Броят на потребителите не е наличен.';

	const handleConfirm = async () => {
		setDeleteErrorMessage('');
		try {
			await deleteBook({ id }).unwrap();
			navigate('/admin/catalogue');
		} catch (err) {
			console.error('Error deleting book:', err);
			setDeleteErrorMessage('Неуспешно изтриване. Моля, опитайте отново.');
		}
	};

	const handleCancel = () => {
		navigate(-1);
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Helmet>
				<title>Изтриване на книга</title>
			</Helmet>
			<Container maxWidth="md" className={classes.container}>
				<Paper
					className={classes.paper}
					aria-describedby="delete-hint delete-a11y-hint"
					aria-busy={isLoading || isDeleting}
				>
					<Typography
						className={classes.title}
						component="h1"
						variant="h4"
						role="alert"
					>
						Изтриване на книга
					</Typography>

					<Typography
						variant="h5"
						role="status"
						aria-live="polite"
						className={`${classes.countText} ${
							countValue > 0 ? classes.warning : ''
						}`}
					>
						{countText}
					</Typography>

					<Typography id="delete-hint">
						Това действие е необратимо. Натиснете „Потвърди“, за да изтриете
						книгата. Натиснете „Отказ“, за да се върнете назад.
					</Typography>
					<Typography className={classes.srOnly} id="delete-a11y-hint">
						Екранен четец: броят на потребителите е показан по-горе и се
						обновява автоматично.
					</Typography>

					{isCountError && (
						<Typography role="alert" color="error">
							Неуспешно зареждане на броя потребители.
						</Typography>
					)}
					{(isDeleteError || deleteErrorMessage) && (
						<Typography role="alert" color="error">
							{deleteErrorMessage ||
								'Неуспешно изтриване. Моля, опитайте отново.'}
						</Typography>
					)}

					<div className={classes.buttons}>
						<Button
							variant="contained"
							color="secondary"
							onClick={handleConfirm}
							disabled={isDeleting}
							aria-label="Потвърди изтриването на книгата"
						>
							Потвърди
						</Button>
						<Button
							variant="outlined"
							onClick={handleCancel}
							disabled={isDeleting}
							aria-label="Отказ и връщане назад"
						>
							Отказ
						</Button>
					</div>
				</Paper>
			</Container>
		</div>
	);
}
